<script setup>
import { Inertia } from '@inertiajs/inertia'
import { PencilAltIcon } from "@heroicons/vue/outline"
import { ExclamationIcon} from  "@heroicons/vue/solid"
import {computed} from 'vue'
import RelatedOrganisation from '@/Components/Models/User/RelatedOrganisation.vue'

import { useAuthStore } from '@/Stores/auth'
const auth = useAuthStore()

const props = defineProps({
	user: {
		type: Object,
		required: true,
	},
})

function editUser(){
	Inertia.visit(route('dashboard.users.edit', props.user.id))
}

/**
 * The displayed user will have permissions on some organisations. We need to check if the currently authed user has `manage_contacts` on
 * any of those organisations. If so, the authed user is able to change the displayed users details.
 */
const can_manage_user = computed(() => {
    // if the user being displayed is the same one as the authed user, they can manage themselves
    if (props.user.id == auth.id) {
        return true;
    }

    // otherwise defer to our auth store
    return auth.can('manage_contacts', 'organisations', props.user.permissions.organisations.map(o => o.id))
});

</script>

<template>
	<SectionCard :title="user.id === auth.user?.id ? 'My Account' : ['admin'].includes(user.role) ? 'Viewing Admin User' : 'Viewing Contact'">
		
		<div class="md:grid md:grid-cols-2">
			<div>
				<div class="mb-4">
					<Label>Name</Label>
					<p>{{ user.name }}</p>
				</div>
			</div>
			<div class="mb-4">
				<Label>Login Email</Label>
				<p>{{ user.email }}</p>
				<div class="flex" v-if="user.email_unverified">
					<ExclamationIcon class="h-4 w-4 mr-1 text-Yellow" /><p class="text-xs">Email address pending verification</p>
				</div>
			</div>
			<div>
				<div class="mb-4">
					<Label>Primary Phone Number</Label>
					<p v-if="!!user.primary_phone_number">{{ user.primary_phone_number }}</p>
					<p v-else class="text-gray-500 text-sm italic">No phone number set</p>
				</div>
			</div>
		</div>

		<template #footer>
			<Button 
				@click="editUser" 
				class="block text-center" 
				color="blue"
				v-if="can_manage_user"
				data-testid="edit-details-btn"
			>
				<PencilAltIcon class="w-4 h-4 mr-2 inline" /> Edit Details
			</Button>
		</template>
	</SectionCard>

	<SectionCard
		:title="'Organisations (' + user.organisations.length + ')'"
		hint="A user can be linked to one or more organisations, and have different roles and permissions to each."
	>
		<div v-if="user.organisations && user.organisations.length" class="mb-4">
			<RelatedOrganisation
				class="mb-4"
				v-for="organisation in user.organisations"
				:user="user"
				:organisation="organisation"
				:key="organisation.id"
			/>
			<div
				v-if="!user.organisations || !user.organisations.length"
				class="text-sm text-gray-500 italic text-center w-full py-8"
			>
				This user is not linked to any organisations
			</div>
		</div>
	</SectionCard>

	<SectionCard title="Admin user" v-if="['admin'].includes(user.role)">
		<p>This user is an admin user. They have full access to the system.</p>
	</SectionCard>
	
</template>